import React, { useState, useRef, useEffect } from 'react';
import { useAccount } from '../contexts/AccountContext';

export default function AccountSwitcher({ showExpanded = true }) {
  const { accounts, activeAccount, hasMultipleAccounts, switchAccount, loading } = useAccount();
  const [isOpen, setIsOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSwitch = async (accountId) => {
    if (accountId === activeAccount?.accountId) {
      setIsOpen(false);
      return;
    }

    try {
      setSwitching(true);
      await switchAccount(accountId);
    } catch (err) {
      console.error('Failed to switch account:', err);
      setSwitching(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-700';
      case 'admin': return 'bg-indigo-100 text-indigo-700';
      case 'manager': return 'bg-green-100 text-green-700';
      case 'assistant': return 'bg-amber-100 text-amber-700';
      case 'viewer': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading || !activeAccount) {
    return null;
  }

  // Don't show account switcher at all if user only has one account
  // Account switcher is only for users whose email is linked to multiple accounts
  // (e.g., they own their own account AND are invited as viewer/admin to another account)
  if (!hasMultipleAccounts) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={switching}
        className={`flex items-center rounded-lg hover:bg-white/50 transition-colors w-full ${showExpanded ? 'gap-2 px-2 py-1' : 'justify-center py-1'}`}
      >
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
          <i className="fas fa-building text-sm"></i>
        </div>
        {showExpanded && (
          <>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-medium text-gray-900 truncate">
                {activeAccount.accountName}
              </p>
            </div>
            <i className={`fas fa-chevron-down text-gray-400 text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
          </>
        )}
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 max-h-64 overflow-y-auto">
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase">Switch Account</p>
          </div>
          
          {accounts.map((account) => (
            <button
              key={account.accountId}
              onClick={() => handleSwitch(account.accountId)}
              disabled={switching}
              className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors ${
                account.accountId === activeAccount.accountId ? 'bg-amber-50' : ''
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-sm ${
                account.isOwner ? 'bg-gradient-to-br from-purple-400 to-purple-600' : 'bg-gradient-to-br from-indigo-400 to-indigo-600'
              }`}>
                {account.accountName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {account.accountName}
                  {account.isOwner && <span className="ml-1 text-xs text-purple-600">(Owner)</span>}
                </p>
                <span className={`inline-block text-xs px-1.5 py-0.5 rounded ${getRoleBadgeColor(account.role)}`}>
                  {account.role}
                </span>
              </div>
              {account.accountId === activeAccount.accountId && (
                <i className="fas fa-check text-amber-500"></i>
              )}
            </button>
          ))}
        </div>
      )}

      {switching && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
          <i className="fas fa-spinner fa-spin text-amber-500"></i>
        </div>
      )}
    </div>
  );
}
